import { useEffect, useRef, useState } from "react";
import CampaignCreate from "./CampaignCreate";
import CampaignDetail from "./CampaignDetail";
import CampaignList from "./CampaignList";
import {
  deleteCampaign,
  getCampaign,
  getCampaigns,
  runCampaignAction,
} from "../../services/campaignsApi";
import { getLeadGroups } from "../../services/leadGroupsApi";
import { normalizeApiError } from "../../utils/errors";

export default function CampaignsTab({ active, refreshSignal, showToast }) {
  const [view, setView] = useState("list"); // "list" | "create" | "detail"
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailCalls, setDetailCalls] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const loadCampaignListRef = useRef(null);
  const refreshCampaignDetailRef = useRef(null);

  async function loadCampaignList() {
    setLoading(true);
    try {
      const data = await getCampaigns();
      setCampaigns(data.campaigns || data.items || []);
    } catch (error) {
      setCampaigns([]);
      showToast(normalizeApiError(error).message, true);
    } finally {
      setLoading(false);
    }
  }

  async function loadGroups() {
    try {
      const data = await getLeadGroups();
      setGroups(data.items || []);
    } catch {
      setGroups([]);
    }
  }

  async function refreshCampaignDetail(campaignId, { silent = false } = {}) {
    if (!silent) setDetailLoading(true);
    try {
      const data = await getCampaign(campaignId);
      setDetail(data.campaign || null);
      setDetailCalls(data.calls || []);
    } catch (error) {
      if (!silent) { setDetail(null); setDetailCalls([]); }
      showToast(normalizeApiError(error).message, true);
    } finally {
      if (!silent) setDetailLoading(false);
    }
  }

  useEffect(() => {
    if (active) {
      loadGroups();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (active && view === "list") {
      loadCampaignListRef.current?.();
    } else if (active && view === "detail" && detail?.id) {
      refreshCampaignDetailRef.current?.(detail.id, { silent: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, refreshSignal, view]);

  useEffect(() => {
    if (!active || view !== "detail" || detail?.status !== "running" || !detail?.id) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      refreshCampaignDetailRef.current?.(detail.id, { silent: true });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [active, view, detail?.id, detail?.status]);

  async function handleOpenDetail(campaignId) {
    setView("detail");
    await refreshCampaignDetail(campaignId);
  }

  async function handleAction(action) {
    if (!detail?.id || actionBusy) return;
    const labels = { start: "started", pause: "paused", resume: "resumed", cancel: "cancelled" };
    setActionBusy(true);
    try {
      const result = await runCampaignAction(detail.id, action);
      let msg = `Campaign ${labels[action] || action}.`;
      if ((action === "start" || action === "resume") && result?.queue?.queued != null) {
        msg += ` ${result.queue.queued} call${result.queue.queued !== 1 ? "s" : ""} queued.`;
      } else if (action === "cancel" && result?.cancelled_calls != null) {
        msg += ` ${result.cancelled_calls} call${result.cancelled_calls !== 1 ? "s" : ""} cancelled.`;
      }
      showToast(msg, false, 4000);
      if (result?.campaign) {
        setDetail(result.campaign);
        setDetailCalls(result.calls || []);
      } else {
        await refreshCampaignDetail(detail.id, { silent: true });
      }
    } catch (error) {
      showToast(normalizeApiError(error).message, true);
    } finally {
      setActionBusy(false);
    }
  }

  async function handleDelete(campaignId) {
    if (!window.confirm("Delete this campaign and all its call records?")) return;
    try {
      await deleteCampaign(campaignId);
      showToast("Campaign deleted.", false, 3000);
      if (view === "detail") {
        setView("list");
        setDetail(null);
        setDetailCalls([]);
        setActionBusy(false);
      }
      loadCampaignList();
    } catch (error) {
      showToast(normalizeApiError(error).message, true);
    }
  }

  loadCampaignListRef.current = loadCampaignList;
  refreshCampaignDetailRef.current = refreshCampaignDetail;

  if (!active) return null;

  return (
    <div className="tab-panel active" id="panel-campaigns" role="tabpanel">
      {view === "list" && (
        <CampaignList
          campaigns={campaigns}
          loading={loading}
          onCreate={() => {
            loadGroups();
            setView("create");
          }}
          onView={handleOpenDetail}
          onDelete={handleDelete}
        />
      )}

      {view === "create" && (
        <CampaignCreate
          groups={groups}
          onCreated={() => {
            setView("list");
            loadCampaignList();
          }}
          onBack={() => {
            setView("list");
            loadCampaignList();
          }}
          showToast={showToast}
        />
      )}

      {view === "detail" && (
        <CampaignDetail
          campaign={detail}
          calls={detailCalls}
          loading={detailLoading}
          actionBusy={actionBusy}
          onBack={() => {
            setView("list");
            setDetail(null);
            setDetailCalls([]);
            setActionBusy(false);
            loadCampaignList();
          }}
          onAction={handleAction}
        />
      )}
    </div>
  );
}
